/**
 * Данные вкладки «Техника» — react-query поверх `/company/machinery` (AK-747).
 *
 * Ключи вложены в префикс карточки хозяйства (`farm-card/<id>`), чтобы одна
 * инвалидация после пакетного действия обновляла и список техники, и счётчик
 * на вкладке: шапка, спорящая с таблицей, — худшее из состояний.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { farmCardKeys } from '../queries';
import {
  machineryParams,
  type FleetFreshness,
  type MachineryCounts,
  type MachineryListPage,
  type MachineryQuery,
  type ModelProfile,
} from './machineryLogic';

export const machineryKeys = {
  all: (farmId: string) => [...farmCardKeys.all(farmId), 'machinery'] as const,
  list: (farmId: string, query: MachineryQuery) =>
    [...farmCardKeys.all(farmId), 'machinery', 'list', query] as const,
  counts: (farmId: string) => [...farmCardKeys.all(farmId), 'machinery', 'counts'] as const,
  freshness: (farmId: string) => [...farmCardKeys.all(farmId), 'machinery', 'freshness'] as const,
  catalog: (search: string) => ['machinery-catalog', search] as const,
  modelProfile: (modelId: string) => ['machinery-model-profile', modelId] as const,
};

/**
 * Список парка СТРАНИЦАМИ.
 *
 * Одной пачкой брать нельзя: у ручки потолок 500 строк, а парк холдинга
 * бывает больше — список молча обрывался бы, без единого намёка, что дальше
 * что-то есть (ровно та беда, что чинили на списке полей). `getNextPageParam`
 * считает сдвиг по УЖЕ полученным строкам, а не по номеру страницы: между
 * запросами кто-то мог списать соседнюю машину, и «страница N+1» уехала бы
 * мимо. Сортировка на бэкенде устойчивая (`name, reg_number, id`) — по этому
 * списку жмут ПАКЕТНЫЕ действия, и уехавшая строка попала бы не в тот пакет.
 */
export function useFarmMachinery(query: MachineryQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: machineryKeys.list(query.farmId, query),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.get<MachineryListPage>(
        `/api/v1/company/machinery?${new URLSearchParams(
          machineryParams(query, pageParam as number),
        )}`,
      ),
    getNextPageParam: (_last, pages) => {
      const loaded = pages.reduce((sum, p) => sum + p.items.length, 0);
      const total = pages[0]?.total ?? 0;
      return loaded < total ? loaded : undefined;
    },
    enabled,
    staleTime: 15_000,
  });
}

/**
 * Счётчик вкладки и разбивка по группам.
 *
 * Отдельный запрос, а не подсчёт по списку: бейдж «Техника (42)» обязан
 * говорить про парк хозяйства, а список на экране отфильтрован и разбит на
 * страницы. Это то же правило, по которому итог в подвале считает выборку.
 */
export function useMachineryCounts(farmId: string) {
  return useQuery({
    queryKey: machineryKeys.counts(farmId),
    queryFn: () =>
      api.get<MachineryCounts>(
        `/api/v1/company/machinery/counts?${new URLSearchParams({ farm_id: farmId })}`,
      ),
    staleTime: 30_000,
  });
}

/**
 * Свежесть парка для шапки вкладки (AK-764, Р29).
 *
 * Отдельный запрос, а не поле в списке: ответ про ХОЗЯЙСТВО целиком (включая
 * пустой парк, где строк нет вовсе и вешать дату не на что), и он обязан
 * пережить любую фильтрацию таблицы.
 */
export function useFleetFreshness(farmId: string, enabled = true) {
  return useQuery({
    queryKey: machineryKeys.freshness(farmId),
    queryFn: () =>
      api.get<FleetFreshness>(
        `/api/v1/company/machinery/fleet-freshness?${new URLSearchParams({ farm_id: farmId })}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

export type CatalogModel = {
  id: string;
  code: string | null;
  name_translations: Record<string, string>;
  machinery_group: string | null;
  type_name: string | null;
  manufacturer_name: string | null;
};

/**
 * Каталог моделей для выпадашки «Модель из каталога».
 *
 * Поиск делает СЕРВЕР (`search` ищет по коду МДМ, наименованию и алиасам, и
 * отдаёт список в алфавитном порядке) — тянуть 657 марок на клиент ради
 * фильтрации нечем оправдать, а алиасы («было наименование» из книги
 * нормативов) на клиенте вообще недоступны.
 */
export function useMachineryCatalog(search: string, enabled: boolean) {
  return useQuery({
    queryKey: machineryKeys.catalog(search),
    queryFn: () =>
      api.get<{ total: number; items: CatalogModel[] }>(
        `/api/v1/reference/machinery-models?${new URLSearchParams({
          limit: '50',
          ...(search.trim() ? { search: search.trim() } : {}),
        })}`,
      ),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Профиль ввода выбранной модели: какие поля показать и что уже знает каталог.
 *
 * Спрашиваем СЕРВЕР, а не решаем на клиенте: признак «ток-оборудование» живёт
 * флагом справочника (`machinery_types.is_stationary`), а на клиенте его
 * пришлось бы угадывать по названию вида. Плюс тот же словарь полей сервер
 * применяет при записи — форма и валидация не разъезжаются по определению.
 */
export function useModelProfile(modelId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: machineryKeys.modelProfile(modelId ?? ''),
    queryFn: () => api.get<ModelProfile>(`/api/v1/company/machinery/model-profile/${modelId}`),
    enabled: enabled && Boolean(modelId),
    staleTime: 5 * 60_000,
  });
}

/**
 * Любая мутация парка. Инвалидация — в `onSettled`, а не в `onSuccess`:
 * при частичном отказе сервер УЖЕ изменился, и экран обязан это показать
 * (канон массовых действий, п. 2).
 */
export function useMachineryMutation<TArgs, TResult>(
  farmId: string,
  run: (args: TArgs) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: farmCardKeys.all(farmId) });
    },
  });
}
